<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {RouterLink, useRouter} from "vue-router";
import {Button, Drawer, Icon, InputText, Menu, ProgressSpinner} from "@/Components/Library";
import {AlphabetList, FilterBar, PageLoading, PageTitle} from "@/Components/Global";
import {getQuestions as getQuestionsFromApi} from "@/Services/questions";
import {useIntersectionObserver, useStorage, watchDebounced} from "@vueuse/core";
import {getAdminGroups} from "@/Services/groups";
import {useMainStore} from "@/Stores/store";
import QuestionResponsesItem from "./QuestionResponsesItem.vue";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import PageContainer from "@/Components/Global/PageContainer.vue";
import ResponsesIndexFilters from "./ResponsesIndexFilters.vue";
import ResponsesMassActions from "@/Pages/User/Responses/ResponsesMassActions.vue";
import User from "@/Models/User";
import {Question} from "@/Types/question";

const $store = useMainStore();
const $user = new User();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const state = reactive({
    error: null,
    show: {
        filters: false,
        questionMenu: null,
        select: false,
    },
    limits: {
        responses_count: 100,
        groups_count: 10,
    },
    actionDialog: {
        visible: false,
        actionText: "",
        itemText: "",
    },
});
const sort = useStorage("responses.index.sort", "-updated_at");

const params = reactive({
    page: 0,
    filter: {
        search: null,
        group_id: [],
        state: "created" as "created" | "archived",
    },
    include: ["files", "stimuli.files"],
    sort: sort.value,
});
const filters = ref({
    type: {
        word: false,
        text: false,
        multiple_choice: false,
        ranking: false,
        slider: false,
        weighting: false,
        voice: false,
    },
    responses_count: [0, 100],
    groups_count: [0, 10],
    group_id: [],
    created_at: null,
    publish_from: null,
    publish_to: null,
    state: "created" as "created" | "archived",
    ownership: "all" as "all" | "mine",
});

const search = ref<string | null>(null);
const questions = ref<Question[]>([]);
const questionsByLetter = computed(() => {
    const questionsByLetter = new Map<string, any[]>();
    questions.value?.forEach((question) => {
        const letter = question.title[0].toUpperCase();
        if (!questionsByLetter.has(letter)) {
            questionsByLetter.set(letter, []);
        }
        questionsByLetter.get(letter).push(question);
    });
    return questionsByLetter;
});
const groups = ref([]);
const loading = ref(true);
const getQuestions = async () => {
    try {
        loading.value = true;
        const {data, total, meta} = await getQuestionsFromApi(params);
        console.log(data);

        if (params.page > 0) {
            questions.value = [...questions.value, ...data];
        } else {
            questions.value = data;
        }
        state.limits.responses_count = meta.max_responses_count;
        if (filters.value.responses_count[1] > state.limits.responses_count) {
            filters.value.responses_count[1] = state.limits.responses_count;
        }
        state.limits.groups_count = meta.max_groups_count;
        if (filters.value.groups_count[1] > state.limits.groups_count) {
            filters.value.groups_count[1] = state.limits.groups_count;
        }

        loadMore.value = data.length && questions.value.length < total;
    } catch (e) {
        console.error(e);
        state.error = e.response?.data?.message || e.message || "An error occurred";
    } finally {
        loading.value = false;
    }
};
getQuestions();

getAdminGroups().then(({data}) => {
    groups.value = data.map((group) => ({id: group.id, name: group.name}));
});

const totalActiveFilters = computed(() => {
    let count = 0;
    if (Object.values(filters.value.type).some((f) => f)) {
        count++;
    }

    if (filters.value.responses_count[0] > 0 || filters.value.responses_count[1] < state.limits.responses_count) {
        count++;
    }

    if (filters.value.groups_count[0] > 0 || filters.value.groups_count[1] < state.limits.groups_count) {
        count++;
    }

    if (filters.value.group_id?.length > 0) {
        count++;
    }

    if (filters.value.created_at) {
        count++;
    }

    if (filters.value.publish_from) {
        count++;
    }

    if (filters.value.publish_to) {
        count++;
    }

    if (filters.value.state !== "created") {
        count++;
    }

    if (filters.value.ownership && filters.value.ownership !== "all") {
        count++;
    }

    return count;
});

const loadMore = ref(false);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {stop} = useIntersectionObserver(
    loadMoreIntersection,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore.value && params.page++,
);

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            type: Object.keys(filters.value.type).filter((key) => filters.value.type[key]),
            search: search.value,
            responses_count: filters.value.responses_count,
            groups_count: filters.value.groups_count,
            group_id: filters.value.group_id,
            created_at: filters.value.created_at,
            publish_from: filters.value.publish_from,
            publish_to: filters.value.publish_to,
            state: filters.value.state,
            ownership: filters.value.ownership,
        },
    };
    Object.assign(params, newParams);
    state.show.filters = false;
    deselectAll();
};

const clearFilters = () => {
    Object.assign(filters.value, {
        type: {
            word: false,
            text: false,
            multiple_choice: false,
            ranking: false,
            slider: false,
            weighting: false,
            voice: false,
        },
        responses_count: [0, 100],
        groups_count: [0, 10],
        group_id: [],
        created_at: null,
        publish_from: null,
        publish_to: null,
        state: "created" as "created" | "archived",
        ownership: "all" as "all" | "mine",
    });
    applyFilters();
};

const sortPanel = useTemplateRef("sortPanel");
const sortMenuItems = ref([
    {
        label: "Alphabetical",
        icon: "mdi:sort-alphabetical-ascending",
        value: "title",
        command: () => {
            sort.value = "title";
        },
    },
    {
        label: "Most recent",
        icon: "ic:round-edit-calendar",
        value: "-updated_at",
        command: () => {
            sort.value = "-updated_at";
        },
    },
    {
        label: "Least recent",
        icon: "ic:round-edit-calendar",
        value: "updated_at",
        command: () => {
            sort.value = "updated_at";
        },
    },
]);

const selectedSortOption = computed(() => {
    return sortMenuItems.value.find((item) => item.value === sort.value);
});

watch(sort, (newSort) => {
    params.sort = newSort;
});

const select = async () => {
    $store.selectedQuestions = [];
    sort.value = "-updated_at";
    await router.replace({query: {select: true}});
};

const cancel = () => {
    $store.selectedQuestions = [];
    router.push({query: {}});
};

const selectAll = () => {
    $store.selectedQuestions = questions.value.filter((q) => q.user_id === $user.id).map((q) => q.id);
};

const deselectAll = () => {
    $store.selectedQuestions = [];
};

watchDebounced(search, applyFilters, {debounce: 500});
watch(params, getQuestions, {deep: true});
</script>
<template>
    <PageContainer>
        <ResponsesMassActions
            v-if="router.currentRoute.value.query.select"
            :state="state"
            :params="params"
            @cancel="cancel"
        />
        <div class="mb-2">
            <PageTitle title="Responses" :show-back-button="false">
                <div class="ml-auto flex gap-3">
                    <Button
                        v-if="!router.currentRoute.value.query.select"
                        class="bg-askr-green !px-2 !py-1 text-xs text-white hover:brightness-110 lg:text-sm"
                        size="small"
                        @click="select"
                    >
                        Select posts
                    </Button>
                    <div v-else class="flex items-center gap-2">
                        <div class="flex items-center gap-1.5">
                            <span class="text-sm">Selected</span>
                            <span
                                class="min-w-8 rounded-full bg-askr-green px-2 py-1 text-center font-semibold text-white"
                            >
                                {{ $store.selectedQuestions.length }}
                            </span>
                        </div>
                        <Button
                            v-tooltip.left="'Select all'"
                            :icon="{icon: 'material-symbols:select-all', width: 30}"
                            class="ml-auto h-8 w-8 flex-none text-askr-green"
                            @click="selectAll"
                        />
                        <Button
                            @click="deselectAll"
                            class="ml-auto h-8 w-8 flex-none text-red-600"
                            :icon="{icon: 'simple-line-icons:minus', width: 30}"
                        />
                    </div>
                </div>
            </PageTitle>
        </div>
        <div class="-sticky top-2 z-10 mb-3 flex items-center gap-2">
            <InputText type="search" v-model="search" placeholder="Search posts" class="grow" />
            <div class="relative">
                <Button
                    :icon="{icon: 'iconoir:sort', width: 26}"
                    :rounded="false"
                    class="aspect-square h-full flex-none bg-askr-green !p-2.5 text-white hover:brightness-110"
                    @click="sortPanel.menu.toggle($event)"
                />
                <div
                    v-if="selectedSortOption"
                    class="pointer-events-none absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500"
                >
                    <Icon :icon="selectedSortOption.icon" class="h-3 w-3 text-white" />
                </div>
            </div>
            <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
        </div>
        <div class="flex items-stretch gap-6">
            <!-- filters -->
            <div class="hidden h-full w-full max-w-sm flex-none 2xl:block">
                <div class="sticky top-2">
                    <ResponsesIndexFilters
                        v-model="filters"
                        :groups="groups"
                        :limits="state.limits"
                        :total-active-filters="totalActiveFilters"
                        @apply="applyFilters"
                        @clear="clearFilters"
                    />
                </div>
            </div>
            <!-- content -->
            <div class="grow">
                <div
                    v-if="!loading && !questions?.length"
                    class="flex grow flex-col items-center justify-center gap-2 p-6 sm:h-auto"
                >
                    <p class="mb-3 text-sm">
                        {{ search || totalActiveFilters ? "No matching posts found." : "You have no posts yet." }}
                    </p>
                    <RouterLink :to="{name: 'questions.create'}">
                        <Button
                            class="bg-askr-green text-white hover:brightness-110"
                            :icon="{
                                icon: 'mdi:plus',
                                width: '24',
                            }"
                        >
                            Create a new question
                        </Button>
                    </RouterLink>
                </div>
                <template v-else>
                    <template v-if="params.sort === 'title'">
                        <template v-for="[letter, questions] in questionsByLetter" :key="letter">
                            <AlphabetList :letter="letter" :vertical="true">
                                <div class="my-2 grid grid-cols-2 gap-2 lg:grid-cols-3 2xl:grid-cols-3" v-auto-animate>
                                    <template v-for="question in questions" :key="question.id">
                                        <QuestionResponsesItem :question="question" />
                                    </template>
                                </div>
                            </AlphabetList>
                        </template>
                    </template>
                    <template v-else>
                        <div class="grid grid-cols-2 gap-2 lg:grid-cols-3 2xl:grid-cols-3" v-auto-animate>
                            <template v-for="question in questions" :key="question.id">
                                <QuestionResponsesItem :question="question" />
                            </template>
                        </div>
                    </template>
                </template>
                <PageLoading v-if="loading" class="pt-16" />
            </div>
        </div>
        <div v-if="!loading && !state.error && loadMore">
            <span ref="loadMoreIntersection"></span>
            <div class="flex justify-center">
                <Button @click="params.page++" :loading="!!loading" :disabled="!!loading || !loadMore">
                    {{ loadMore || !questions.length ? "Load more" : "No more results" }}
                </Button>
            </div>
        </div>
        <FilterBar v-model="state.show.filters" :total-active-filters="totalActiveFilters"></FilterBar>
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh w-full max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <ResponsesIndexFilters
                    v-model="filters"
                    :groups="groups"
                    :limits="state.limits"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
        <Dialog
            :header="`${state.actionDialog.actionText} ${state.actionDialog.itemText}`"
            modal
            :closable="false"
            :draggable="false"
            v-model:visible="state.actionDialog.visible"
            class="!max-w-48"
            :pt:title:class="`!hidden`"
        >
            <div class="relative">
                <ProgressSpinner class="aspect-square h-full w-full" />
                <div
                    class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center text-xs"
                >
                    <span>{{ state.actionDialog.actionText }}</span>
                    <span>{{ state.actionDialog.itemText }}</span>
                </div>
            </div>
        </Dialog>
    </PageContainer>
</template>
<style scoped></style>
